<template>
  <div class="company-layout" :style="rootStyle">
    <PaletteButton
      isPCPreview
      @changeDevice="handleChangeDevice"
      @closePreview="handleClosePreview"
    />
    <div class="layout-head-wrapper">
      <PersonalCommonHeader
        v-if="companyInfo.id"
        isPreview
        :companyId="companyId"
        :customPage="customPage"
        :class="fixedHeader && 'fixed-header'"
        @changeLanguage="handleChangeLanguage"
      ></PersonalCommonHeader>
    </div>
    <div v-if="!loading" class="layout-banner">
      <div v-if="bannerSetting" class="banner-wrapper">
        <Banner class="banner" hiddenIndicator slide-arrow="hover" :bannerSetting="bannerSetting"/>
      </div>
    </div>
    <div class="layout-body">
      <JobDetail
        v-if="!loading"
        class="body-content"
        isPreview
        :previewJobDetailInfo="jobDetailInfo"
        :previewJobAndCompanyInfo="jobAndCompanyInfo"
        :previewCompanyInfo="companyDetailInfo"
        :previewJobId="jobId"
        :company="company"
        :brandingPageWebpageItems="brandingPageWebpageItems"
      ></JobDetail>
    </div>
    <CommonFooter v-if="showCustomFooterNavigation" class="custom-footer"></CommonFooter>
  </div>
</template>

<script>
import { addI18nProperty, isEn } from '@/i18n'
import { mapActions, mapGetters } from 'vuex'
import { ACCOUNT_TYPE } from '@/constant/common'
import { COMPANY_PAGE_TYPE } from '@/constant/company'
import _ from 'lodash'
import Banner from '@/components/company/detail/banner.vue'
import JobDetail from '@/views/company/pc/jobDetail/index.vue'
import PaletteButton from './paletteButton.vue'
import PersonalCommonHeader from '@/components/layout/header/personalCommonHeader.vue'
import CommonFooter from '@/components/layout/footer/commonFooter.vue'
import mixin from '@/views/personalNew/layout/mixin'

export default {
  components: {
    Banner,
    JobDetail,
    PaletteButton,
    PersonalCommonHeader,
    CommonFooter
  },
  props: {
    jobDetailInfo: {
      required: true
    },
    jobAndCompanyInfo: {
      required: true
    },
    companyDetailInfo: {
      required: true
    },
    companyId: {
      required: true
    },
    jobId: {
      required: true
    }
  },
  metaInfo () {
    return {
      style: [{
        cssText: `
          body {
            font-size: 16px;
            color: #303133;
            line-height: 22px;
            --custom-color: ${this.company.custom_color};
          }
          * {
            box-sizing: border-box;
          }
        `
      }],
      link: this.metaInfoLinkList
    }
  },
  mixins: [mixin],
  data () {
    return {
      loading: false,
      company: {
        logo: null
      },
      customPage: {},
      showChatbot: false,
      showChatbotMessageBox: false,
      userId: '',
      brandingPageWebpageItems: null,
      metaInfoLinkList: []
    }
  },
  computed: {
    loginPath () {
      return `/passport/${ACCOUNT_TYPE.CANDIDATE}/register?back=${this.$route.fullPath}`
    },
    isLogin () {
      const account = this.getCurrentAccount()
      return Boolean(account && account.id)
    },
    landingPage () {
      return this.customPage?.landing_page
    },
    brandingPage () {
      return this.customPage?.branding_page
    },
    bannerSetting () {
      if (this.brandingPage) {
        return this.brandingPage.pc_branding_banner
      }
      return null
    },
    anotherLanguage () {
      if (isEn()) {
        return '简体中文'
      } else {
        return 'English'
      }
    },
    menuList () {
      const totalMenuList = [
        {
          hidden: !this.brandingPage,
          label: this.brandingPage?.i18nName,
          pageNum: this.brandingPage?.page_number,
          name: COMPANY_PAGE_TYPE.BRANDING
        },
        {
          hidden: !this.landingPage,
          label: this.landingPage?.i18nName,
          pageNum: this.landingPage?.page_number,
          name: COMPANY_PAGE_TYPE.LANDING
        },
        {
          // path: jobListRoute.path,
          labelI18nKey: 'company.menu.jobList',
          name: COMPANY_PAGE_TYPE.JOB_LIST
        }
      ]
      const list = []
      _.forEach(totalMenuList, menu => {
        if (menu.hidden) {
          return
        }
        list.push(menu)
      })
      return list
    },
    showCustomFooterNavigation () {
      const footerContent = this.companyThemeSetting?.footerSetting?.footerContent
      if (footerContent && footerContent.length > 0) {
        return true
      }
      return false
    },
    fixedHeader () {
      return this.hasCustomTheme ? this.companyThemeSetting.headerSetting?.fixedHeader : false
    },
    rootStyle () {
      const style = {
        '--header-height': this.headerSetting.headerHeight ? `${this.headerSetting.headerHeight}px` : null,
        '--custom-color': this.company.custom_color
      }
      return style
    }
  },
  created () {
    this.initPageData()
  },
  methods: {
    ...mapActions('common', ['changeLanguage']),
    ...mapActions('company', ['getCompanyBaseInfo', 'getCompanyBrandingPageInfo', 'getCompanyTheme']),
    ...mapGetters('account', ['getCurrentAccount']),
    handleClosePreview () {
      this.$emit('closePreview')
    },
    handleChangeDevice () {
      this.$emit('changeDevice')
    },
    async initPageData () {
      this.loading = true
      this.$eventBus.$emit('showLoading', true)
      try {
        await Promise.all([
          this.loadCompanyInfo(),
          this.loadCompanyPageInfo(),
          this.getCompanyTheme({ companyId: this.companyId })
        ])
      } finally {
        this.loading = false
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadCompanyInfo () {
      const resp = await this.getCompanyBaseInfo({ id: this.companyId, params: { simple_info: 1 } })
      if (resp) {
        this.company = addI18nProperty(resp, 'i18nName', 'cn_name', 'name')
        this.showChatbot = resp.show_chatbot
        if (this.showChatbot) {
          let baseUrl = process.env.VUE_APP_STYLE_BASE_URL
          if (baseUrl.endsWith('/')) {
            baseUrl = baseUrl.substring(0, baseUrl.length - 1)
          }
          this.metaInfoLinkList.push({
            rel: 'stylesheet',
            href: `${baseUrl}/static/style/henkel-bot.css`
          })
        }
      }
    },
    async loadCompanyPageInfo () {
      const resp = await this.getCompanyBrandingPageInfo({
        company_id: this.companyId
      })
      if (resp) {
        const brandingPage = resp.branding_page
        const landingPage = resp.landing_page
        if (brandingPage) {
          resp.branding_page = addI18nProperty(brandingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
          this.brandingPageWebpageItems = brandingPage.webpage_items ? JSON.parse(brandingPage.webpage_items) : []
        }
        if (landingPage) {
          resp.landing_page = addI18nProperty(landingPage, 'i18nName', 'menu_cn_name', 'menu_en_name')
        }
        this.customPage = resp
      }
    },
    async handleChangeLanguage () {
      this.$eventBus.$emit('showLoading', true)
      try {
        await this.loadCompanyPageInfo()
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    isActive (menu) {
      if (menu.name === COMPANY_PAGE_TYPE.JOB_LIST) {
        return true
      }
      return false
    },
    handleOpenHome () {
      this.$router.push({ path: '/recruiting/personal/home', query: { company_id: this.companyId } })
    }
  },
  watch: {
  }
}
</script>

<style lang="scss" scoped>
$navigation-size: 24px;
$safe-blank: 15px;

.company-layout {
  margin: 0 auto;
  min-height: 100vh;

  .layout-head-wrapper {
    height: var(--header-height, 124px);

    .layout-head {
      height: var(--header-height, 124px);
      line-height: var(--header-height, 124px);
      padding: 0 106px;

      ::v-deep {
        .company-info {
          .has-company-logo {
            width: var(--logo-width, 80px);
            height: var(--logo-width, 80px);
          }
        }

        .menu-list {
          margin-left: 126px;
        }

        &.center-style{
          .menu-list {
            margin-left: 0;
          }
        }
      }
    }

    .fixed-header {
      position: fixed;
      top: 56px;
      left: 0;
      right: 0;
      z-index: 3;
    }
  }

  .layout-banner {
    .banner-wrapper {
      .banner {
        display: block;
        width: 100%;
        ::v-deep {
          .slide-container {
            .el-carousel__item {
              display: block;
              img {
                width: 100%;
              }
            }
          }
        }
      }
    }
  }

  .layout-body {
    padding: 40px 106px 24px;

    .body-content {
      margin: 0 auto;
    }
  }
}
</style>
